import { Trash2 } from "lucide-react";

import { cn } from "../../../../lib/utils";
import ButtonV2 from "../../../../components/ui/button/v2";
import {
  Account,
  AccountCategory,
  AccountType,
  ACCOUNT_CATEGORIES,
  ACCOUNT_TYPE_OPTIONS,
  buildAccountCategoryOptions,
} from "../../../../api/accounts";
import FormField, { useTouched } from "./FormField";

export interface AccountRowProps {
  value: Account;
  accounts: Account[];
  showErrors: boolean;
  showLabels?: boolean;
  disabled?: boolean;
  onChange: (next: Account) => void;
  onRemove: () => void;
  canRemove?: boolean;
}

export const getAccountRowErrors = (
  row: Account,
  all: Account[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!row.ext_account_number.trim()) {
    errors.extAccountNumber = "Account number is required.";
  } else if (
    all.filter(
      (a) =>
        a.ext_account_number.trim().toLowerCase() ===
        row.ext_account_number.trim().toLowerCase(),
    ).length > 1
  ) {
    errors.extAccountNumber = "Account number must be unique.";
  }
  if (!row.account_type) errors.accountType = "Account type is required.";
  if (!row.account_category) {
    errors.accountCategory = "Account category is required.";
  } else if (
    row.account_type &&
    !(ACCOUNT_CATEGORIES[row.account_type as AccountType] ?? []).includes(
      row.account_category as AccountCategory,
    )
  ) {
    errors.accountCategory = "Account category is not valid for this type.";
  }
  if (!row.account_name.trim()) {
    errors.accountName = "Account name is required.";
  }
  return errors;
};

export const isAccountRowValid = (row: Account, all: Account[]) =>
  Object.keys(getAccountRowErrors(row, all)).length === 0;

const AccountRow: React.FC<AccountRowProps> = ({
  value,
  accounts,
  showErrors,
  showLabels = true,
  disabled = false,
  onChange,
  onRemove,
  canRemove = true,
}) => {
  const { isTouched, markTouched } = useTouched();
  const errors = getAccountRowErrors(value, accounts);
  const err = (f: string) => (showErrors || isTouched(f) ? errors[f] : undefined);
  const categoryOptions = buildAccountCategoryOptions(value.account_type);

  const patch = (partial: Partial<Account>) => onChange({ ...value, ...partial });

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 items-start">
      <FormField
        type="text"
        label="Account Number"
        required
        placeholder="Enter account number"
        value={value.ext_account_number}
        error={err("extAccountNumber")}
        hideLabel={!showLabels}
        disabled={disabled}
        onChange={(v) => patch({ ext_account_number: v })}
        onBlur={() => markTouched("extAccountNumber")}
      />
      <FormField
        type="select"
        label="Account Type"
        required
        placeholder="Select type"
        value={value.account_type}
        options={ACCOUNT_TYPE_OPTIONS}
        error={err("accountType")}
        hideLabel={!showLabels}
        disabled={disabled}
        onChange={(v) =>
          patch({
            account_type: v as AccountType | "",
            account_category: "",
          })
        }
        onBlur={() => markTouched("accountType")}
      />
      <FormField
        type="select"
        label="Account Category"
        required
        placeholder="Select category"
        value={value.account_category}
        options={categoryOptions}
        error={err("accountCategory")}
        hideLabel={!showLabels}
        disabled={disabled || !value.account_type}
        onChange={(v) => patch({ account_category: v as Account["account_category"] })}
        onBlur={() => markTouched("accountCategory")}
      />
      <FormField
        type="text"
        label="Account Name"
        required
        placeholder="Enter name"
        value={value.account_name}
        error={err("accountName")}
        hideLabel={!showLabels}
        disabled={disabled}
        onChange={(v) => patch({ account_name: v })}
        onBlur={() => markTouched("accountName")}
      />
      <div className={cn("flex items-center", showLabels && "pt-5")}>
        <ButtonV2
          variant="outline"
          size="sm"
          onClick={onRemove}
          disabled={disabled || !canRemove}
          title="Delete account"
          className="h-9 px-2 rounded-md bg-white border border-gray-300 text-red-500 hover:bg-red-500 hover:text-white hover:border-red-500 disabled:opacity-40"
          prefix={<Trash2 width="16" height="16" />}
        />
      </div>
    </div>
  );
};

export default AccountRow;
